import { Link } from 'react-router-dom'

// Visible breadcrumb nav. Pair with a BreadcrumbList JSON-LD (emitted by the
// containing page) so Google gets both the schema and the user-visible path.
export interface BreadcrumbItem {
  label: string
  to?: string // omit on the current page
}

export default function Breadcrumbs({
  items,
  // Default clears the fixed nav, for a page whose first section starts under
  // it. A page with a full-bleed hero puts the trail BELOW the photograph and
  // passes its own spacing instead.
  className = 'max-w-4xl mx-auto px-6 pt-24 sm:pt-28',
  // `onDusk` matches SectionHeader's convention: the dusk tokens never invert,
  // so a trail on a DuskPanel cannot use `text-muted` / `text-ink`.
  tone = 'default',
}: {
  items: BreadcrumbItem[]
  className?: string
  tone?: 'default' | 'onDusk'
}) {
  const onDusk = tone === 'onDusk'
  const baseClass = onDusk ? 'text-on-dusk/60' : 'text-muted'
  const linkClass = onDusk ? 'hover:text-gold transition-colors' : 'hover:text-terracotta transition-colors'
  const currentClass = onDusk ? 'text-on-dusk/85' : 'text-ink/70'
  const dividerClass = onDusk ? 'text-on-dusk/30' : 'text-muted/50'

  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className={`flex items-center gap-2 text-xs flex-wrap ${baseClass}`}>
        {items.map((item, i) => {
          const isLast = i === items.length - 1
          return (
            <li key={i} className="flex items-center gap-2">
              {item.to && !isLast ? (
                <Link to={item.to} className={linkClass}>
                  {item.label}
                </Link>
              ) : (
                <span aria-current={isLast ? 'page' : undefined} className={isLast ? currentClass : ''}>
                  {item.label}
                </span>
              )}
              {!isLast && <span className={dividerClass}>/</span>}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
